// components/EmptyState.js
// Reusable empty states for Deals, Leads, Introducers, Commission, and Team pages.

import React from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Generic base ─────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, description, primaryAction, secondaryAction }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div className="text-6xl mb-4 select-none">{icon}</div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 max-w-sm mb-6 leading-relaxed">{description}</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {primaryAction && (
          <button
            onClick={primaryAction.onClick}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition shadow-sm"
          >
            {primaryAction.icon && <span>{primaryAction.icon}</span>}
            {primaryAction.label}
          </button>
        )}
        {secondaryAction && (
          <button
            onClick={secondaryAction.onClick}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium transition"
          >
            {secondaryAction.icon && <span>{secondaryAction.icon}</span>}
            {secondaryAction.label}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Specific empty states ─────────────────────────────────────────────────────

/** No deals / pipeline empty */
export function EmptyDeals({ onAddDeal }) {
  return (
    <EmptyState
      icon="📋"
      title="No deals yet"
      description="Your pipeline is empty. Add your first deal manually or ask your introducers to start referring clients."
      primaryAction={{
        label: 'Add a deal',
        icon:  '＋',
        onClick: onAddDeal,
      }}
    />
  );
}

/** No leads */
export function EmptyLeads({ onAddLead, onInviteIntroducer }) {
  return (
    <EmptyState
      icon="🎯"
      title="No leads yet"
      description="Leads come in when introducers refer clients through your network. Invite your first introducer to get started."
      primaryAction={{
        label:   'Invite an introducer',
        icon:    '📨',
        onClick: onInviteIntroducer,
      }}
      secondaryAction={{
        label:   'Add a lead manually',
        onClick: onAddLead,
      }}
    />
  );
}

/** No introducers — shown on the Introducers list page */
export function EmptyIntroducers({ onInvite }) {
  return (
    <EmptyState
      icon="🤝"
      title="No introducers yet"
      description="Grow your network by inviting solicitors, estate agents, accountants, and other professionals who can refer clients to you."
      primaryAction={{
        label:   'Send your first invite',
        icon:    '📨',
        onClick: onInvite,
      }}
    />
  );
}

/** No commission data */
export function EmptyCommission() {
  return (
    <EmptyState
      icon="💷"
      title="No commission data yet"
      description="Commission figures will appear here as deals are completed and logged in the system."
    />
  );
}

/** No team members (for managers/owners looking at their team) */
export function EmptyTeam({ onInviteMember, role }) {
  const isOwner = ['platform_owner','broker_owner'].includes(role);
  return (
    <EmptyState
      icon="👥"
      title="No team members yet"
      description={
        isOwner
          ? "Build your team by adding managers and advisers. They'll be able to manage leads and introducers under your company."
          : "You don't have any advisers assigned to you yet."
      }
      primaryAction={isOwner ? {
        label:   'Add a team member',
        icon:    '＋',
        onClick: onInviteMember,
      } : undefined}
    />
  );
}

/** No pending introducer applications */
export function EmptyPendingApplications() {
  return (
    <EmptyState
      icon="📬"
      title="No pending applications"
      description="All caught up! New introducer applications will appear here for your review."
    />
  );
}

/** No notifications */
export function EmptyNotifications() {
  return (
    <EmptyState
      icon="🔔"
      title="You're all caught up"
      description="No new notifications. We'll let you know when deals are updated, introducers join, or actions need your attention."
    />
  );
}

/** Search/filter returned no results */
export function EmptySearchResults({ query, onClear }) {
  return (
    <EmptyState
      icon="🔍"
      title={`No results for "${query}"`}
      description="Try a different search term or clear your filters."
      primaryAction={{
        label:   'Clear search',
        onClick: onClear,
      }}
    />
  );
}

/** Generic fallback */
export function EmptyGeneric({ title = 'Nothing here yet', description = '' }) {
  return (
    <EmptyState
      icon="📭"
      title={title}
      description={description || 'Content will appear here once data is available.'}
    />
  );
}

// ─── Usage guide (for reference, not rendered) ────────────────────────────────
/*
USAGE:

import { EmptyDeals, EmptyLeads, EmptyIntroducers } from '../components/EmptyState';

// In your list component:
if (!deals.length) return <EmptyDeals onAddDeal={() => setShowAddModal(true)} />;

// Introducers page:
if (!introducers.length) return (
  <EmptyIntroducers onInvite={() => navigate('/invite')} />
);

// With search:
if (!filtered.length && searchQuery) return (
  <EmptySearchResults query={searchQuery} onClear={() => setSearchQuery('')} />
);
*/
